import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box, Text, useThemeUI } from "theme-ui";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import isString from "lodash/fp/isString";
import map from "lodash/fp/map";
import update from "lodash/fp/update";

import { Button, Input, LoadingIndicator } from "components";
import ModalCenter from "../ModalCenter";
import * as styles from "./ModalShare.styles";
import { useTranslation } from "hooks";
import {
  shareRecipe,
  createRecipeAndImage,
  marketingEmailSubscription,
} from "api";

import { CheckmarkIcon } from "components/icons";
import { Checkbox } from "components/Forms";
import {
  selectAddToOrderValues,
  selectProductImages,
  selectUserChoicesForUi,
  selectProductExpectedDelivery,
  selectProductCost,
  selectSelectedProduct,
  selectUIVersion,
  selectProductId,
} from "store/app";

export default function ModalShare({
  isTopMostModal,
  title,
  onClose,
  shareYourCustomDesignObj,
}) {
  const { t } = useTranslation();
  const {
    theme: { isMobile },
  } = useThemeUI();

  const [abortControllerID, setAbortControllerID] = useState(0);
  const [abortController, setAbortController] = useState(null);
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  const userChoiceValues = useSelector(selectAddToOrderValues);
  const id = useSelector(selectProductId);
  const locale = useSelector((state) => state.app.locale);
  const userChoiceValuesForUI = useSelector(selectUserChoicesForUi(t));
  const analyticsObj = JSON.stringify(userChoiceValues);
  const expectedDelivery = useSelector(selectProductExpectedDelivery);
  const cost = useSelector(selectProductCost);
  const selectedProduct = useSelector(selectSelectedProduct);
  const UIVersion = useSelector(selectUIVersion);

  const productImages = useSelector(selectProductImages);
  const [checked, setChecked] = useState(true);

  const images = productImages.map(
    update("images", flow(map(get("url")), filter(isString)))
  );

  const handleChange = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortControllerID]);

  const handleSubmit = (evt) => {
    evt.preventDefault();
    const controller = new AbortController();
    setAbortController(controller);
    setAbortControllerID(abortControllerID + 1);
    setIsLoading(true);

    createRecipeAndImage({
      userChoiceValues,
      signal: controller.signal,
      images,
    })
      .then(({ recipeID, finalImages }) => {
        return shareRecipe({ signal: controller.signal })({
          recipeID,
          value,
          final_image: finalImages,
          userChoiceValuesForUI,
          expectedDelivery,
          cost,
          selectedProduct,
          id,
          locale: locale.split("-")[0],
        });
      })
      .then((res) => {
        setShareSuccess(true);
        setIsLoading(false);
        console.log("share working", res.data, locale);
        if (checked) {
          if (locale === "en-CA") {
            return marketingEmailSubscription()({
              url: process.env.REACT_APP_MARKETING_URL_CA,
              list_id: process.env.REACT_APP_KLAVIYO_LIST_ID_CA,
              custom_source: "mybauer_share",
              email: value,
            });
          } else {
            return marketingEmailSubscription()({
              url: process.env.REACT_APP_MARKETING_URL_US,
              list_id: process.env.REACT_APP_KLAVIYO_LIST_ID_US,
              custom_source: "mybauer_share",
              email: value,
            });
          }
        }
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <ModalCenter isTopMostModal={isTopMostModal} onClose={onClose}>
      <Box sx={styles.contentContainer}>
        {isLoading && (
          <Box sx={styles.loadingIndicatorContainer}>
            <LoadingIndicator />
          </Box>
        )}
        <Text variant="h4Light" sx={styles.modalTitle}>
          {title}
        </Text>

        {shareSuccess && (
          <>
            <Text as="div" variant="copy2Medium" sx={styles.descriptionText}>
              <Box sx={styles.checkmarkIcon}>
                <CheckmarkIcon />
              </Box>
              {shareYourCustomDesignObj.shareConfirmation}
            </Text>
            <Text as="div" variant="copy2Medium" sx={styles.subText}>
              {value}
            </Text>
          </>
        )}

        {!shareSuccess && (
          <>
            <Text as="div" variant="copy3Light" sx={styles.descriptionText}>
              {shareYourCustomDesignObj.text}
            </Text>
            <Text as="div" variant="copy2Medium" sx={styles.subText}>
              {shareYourCustomDesignObj.subTitle}
            </Text>

            {UIVersion === 2 && (
              <Box as="form" onSubmit={handleSubmit}>
                <Input
                  sx={styles.emailInput}
                  placeholder="recipient@domain.com"
                  variant="round"
                  onChange={setValue}
                  value={value}
                />

                <Box sx={styles.buttonsContainer}>
                  <Button
                    variant="secondary"
                    size="medium"
                    isRound
                    onClick={onClose}
                    rel="noopener noreferrer"
                  >
                    {t("close")}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="medium"
                    isRound
                    theme="dark"
                    disabled={!Boolean(value)}
                  >
                    {t("send")}
                  </Button>
                </Box>
              </Box>
            )}

            {UIVersion !== 2 && (
              <Box
                as="form"
                onSubmit={handleSubmit}
                sx={styles.emailInputContainer}
              >
                <Box sx={styles.emailLength}>
                  <Input
                    placeholder="recipient@domain.com"
                    onChange={setValue}
                    value={value}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="primary"
                  sx={styles.sendButton}
                  size="medium"
                  theme="dark"
                  disabled={!value || isLoading}
                  data-attr-track={value && true}
                  data-attr-analytics-trigger="share"
                  data-attr-analytics-value={analyticsObj}
                  data-attr-analytics-email={value}
                >
                  {t("Send")}
                </Button>
              </Box>
            )}
            <Box sx={styles.marketingBox}>
              <Box sx={styles.marketingCheckbox}>
                <Checkbox
                  size={isMobile ? "small" : "medium"}
                  value={checked}
                  onChange={handleChange}
                  defaultValue
                />
              </Box>
              <Text
                as="div"
                variant="copy2Medium"
                sx={styles.marketingSubTitle}
              >
                {shareYourCustomDesignObj.marketingSubTitle}
              </Text>
            </Box>
          </>
        )}
      </Box>
    </ModalCenter>
  );
}
